import React, { useCallback, useState, useEffect, Fragment } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, useFieldArray } from 'react-hook-form';
import UserAPI from '../../api/User';
import SponsorshipEmailListPopup from '../../../src/components/popup/SponsorshipEmailListPopup';
import Utils from '../../utils/utils';
import Loading from '../common/Loading';
import { Icon } from '@homeexchange/design';

import i18n from '../../i18n';

const SponsorshipEmailPopup = (props) => {
    const [loadGapi, setLoadGapi] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    // const [showButtons, setShowButtons] = useState(false);
    const [responseFields, setResponseFields] = useState([]);
    const [defaultValueFromGAPI, setDefaultValueFromGAPI] = useState([]);
    const { control, handleSubmit, reset, watch, register, setValue, formState } = useForm({
        defaultValues: {
            default: [{ email: '' }]
        }
    });
    const { fields } = useFieldArray({
        control,
        name: 'default'
    });
    const apiKey = process.env.GOOGLE_API_PEOPLE_KEY;
    const discoveryDocs = [process.env.DISCOVERY_DOCS_KEY];
    const clientId = process.env.PEOPLE_CLIENT_ID;
    const scopes = `https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/contacts`;

    const makeApiCall = () => {
        window.gapi.client.people.people
            .get({
                resourceName: 'people/me',
                personFields: 'names,emailAddresses,photos'
            })
            .then(
                (response) => {
                    console.log('people response: ', response);
                    setResponseFields([
                        {
                            id: 'test',
                            photo: 'test',
                            email: 'test@test.com',
                            name: 'Test Test'
                        },
                        {
                            id: response.result.resourceName,
                            photo: response.result.photos[0].url,
                            email: response.result.emailAddresses[0].value,
                            name: response.result.names[0].displayName
                        }
                    ]);
                },
                (err) => {
                    console.log('people err: ', err);
                }
            );
    };

    const updateSigninStatus = (isSignedIn) => {
        if (isSignedIn) {
            props.setReadyGapi(true);
            makeApiCall();
        }
    };

    const initClient = () => {
        window.gapi.client
            .init({
                apiKey,
                discoveryDocs,
                clientId,
                scope: scopes
            })
            .then(() => {
                window.gapi.auth2.getAuthInstance().isSignedIn.listen(updateSigninStatus);
                updateSigninStatus(window.gapi.auth2.getAuthInstance().isSignedIn.get());
            });
    };

    const handleAuthClick = () => {
        window.gapi.auth2.getAuthInstance().signIn();
    };

    useEffect(() => {
        if (loadGapi) {
            const script = document.createElement('script');
            script.src = 'https://apis.google.com/js/api.js';
            script.async = true;
            script.defer = true;

            script.addEventListener('load', () => {
                window.gapi.load('client:auth2', initClient);
            });

            document.body.appendChild(script);
            return () => {
                document.body.removeChild(script);
            };
        }
    }, [loadGapi]);

    useEffect(() => {
        if (props.readyGapi) {
            handleAuthClick();
            // only to turn off warning
            setLoadGapi(true);
        }
    }, [props.readyGapi]);

    useEffect(() => {
        if (defaultValueFromGAPI.length > 0) {
            reset({ default: defaultValueFromGAPI });
        }
    }, [defaultValueFromGAPI]);

    const emails = watch('default')[0]
        .email.replace(/ /g, '')
        .split(',')
        .filter((i) => i.length).length;

    const onSubmit = (data) => {
        UserAPI.sendInvite(data).then(
            (response) => {
                const content = (
                    <div>
                        <div className="email-sponsorship-success">
                            {response.success.length > 0 &&
                                (response.success.length > 1
                                    ? `${i18n.t(
                                          'sponsorship:invitation_sent_plural'
                                      )} ${response.success.join(', ')}`
                                    : `${i18n.t('sponsorship:invitation_sent')} ${response.success[0]}`)}
                        </div>
                        <div className="email-sponsorship-already-member">
                            {response['already-member'].length > 0 &&
                                (response['already-member'].length > 1
                                    ? i18n.t('sponsorship:already_member_plural', {
                                          emails: response['already-member'].join(', ')
                                      })
                                    : props.user.get('email') === response['already-member'][0]
                                    ? i18n.t('sponsorship:cant_refer_yourself', {
                                          email: response['already-member'][0]
                                      })
                                    : i18n.t('sponsorship:already_member', {
                                          email: response['already-member'][0]
                                      }))}
                        </div>
                        <div className="email-sponsorship-errors">
                            {response.error.length > 0 &&
                                (response.error.length > 1
                                    ? `${i18n.t('sponsorship:invalid_email_plural')} ${response.error.join(
                                          ', '
                                      )}`
                                    : `${i18n.t('sponsorship:invalid_email')} ${response.error[0]}`)}
                        </div>
                    </div>
                );
                const title =
                    response.success.length > 0
                        ? response.success.length > 1
                            ? i18n.t('sponsorship:invitation_plural')
                            : i18n.t('sponsorship:invitation')
                        : i18n.t('common:problem_occured');
                sweetAlert({
                    title,
                    content,
                    icon: response.success.length === 0 ? 'error' : 'success'
                });
            },
            (error) => {
                const message = Utils.getErrorMessage(error);
                const errorSweet = sweetAlert({
                    icon: 'error',
                    title: i18n.t('common:problem_occured'),
                    message
                });
                if (error.status == 401) {
                    return errorSweet;
                } else {
                    return errorSweet;
                }
            }
        );
        props.closeModal();
    };

    const addFieldsFromGAPI = useCallback((data) => {
        setDefaultValueFromGAPI(data);
        props.setReadyGapi(false);
    }, []);

    return (
        <Modal.Body>
            {props.readyGapi ? (
                responseFields.length > 0 ? (
                    <SponsorshipEmailListPopup
                        fields={responseFields}
                        addFieldsFromGAPI={addFieldsFromGAPI}
                    />
                ) : (
                    <Loading />
                )
            ) : (
                <div>
                    <h4>{i18n.t('sponsorship:invite_by_email')}</h4>
                    <div
                        className="modal-email"
                        dangerouslySetInnerHTML={{
                            __html: i18n.t('sponsorship:enter_email_v1')
                        }}
                    />
                    <form id="email-sponsorship-form" onSubmit={handleSubmit(onSubmit)}>
                        <div className="modal-email-inputs">
                            {fields.map((item, index) => (
                                <Fragment key={item.id}>
                                    <div className="modal-email-input">
                                        <div className="modal-email-icon">
                                            <Icon name="Envelope" />
                                        </div>
                                        <input
                                            name={`default[${index}].email`}
                                            defaultValue={`${item.email}`}
                                            // eslint-disable-next-line react/no-unknown-property
                                            control={control}
                                            ref={register()}
                                            readOnly={readOnly}
                                            /* eslint-disable react/jsx-no-bind */
                                            onChange={(e) =>
                                                setValue('default', [{ email: e.target.value.trim() }])
                                            }
                                            onFocus={() => setReadOnly(false)}
                                            onBlur={() => setReadOnly(true)}
                                        />
                                        {emails > 0 && (
                                            <div className="modal-email-number">{`(${emails})`}</div>
                                        )}
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                        <button
                            type="submit"
                            id="send-email-sponsorship"
                            className="btn btn-primary"
                            disabled={!formState.dirty}
                        >
                            {i18n.t('sponsorship:send_invite')}
                        </button>
                    </form>
                </div>
            )}
        </Modal.Body>
    );
};

SponsorshipEmailPopup.propTypes = {
    closeModal: PropTypes.func,
    setReadyGapi: PropTypes.func,
    readyGapi: PropTypes.bool,
    user: PropTypes.object
};

export default SponsorshipEmailPopup;
